import Colors from '@constants/Colors';
import { useSessionListDataFetching } from '@hooks/useSessionListDataFetching';
import { CardListDateHeader } from '@molecules/CardListDateHeader';
import type { FlashListProps, ViewToken } from '@shopify/flash-list';
import { FlashList, MasonryFlashList } from '@shopify/flash-list';
import type { SessionOutput } from '@sunrise-ui/api/events/api';
import {
  CacheAwareConfirmedSessionCard,
  SessionCardStatus,
} from '@sunrise-ui/composites';
import type { WithResponsive } from '@sunrise-ui/primitives';
import {
  CARD_MARGIN,
  CacheAvailabilityProvider,
  Loader,
  TextReg,
  useDynamicStyles,
  useMediaQueries,
  useScrollPositionPreservation,
} from '@sunrise-ui/primitives';
import type { FlashListItem } from '@utils/sessionList';
import {
  createFlashListData,
  createSessionMarkedDates,
  groupSessionsByDate,
  scrollToFlashListDate,
} from '@utils/sessionList';
import { DateTime } from 'luxon';
import React, { useMemo, useRef } from 'react';
import { RefreshControl, StyleSheet, View } from 'react-native';

type Props = {
  sessions?: SessionOutput[];
  isLoading?: boolean;
  scrollKey: string;
  pageCommunityId?: string;
  userId?: string;
  onRefresh?: () => void;
  refreshing?: boolean;
  sortDirection?: 'asc' | 'desc';
  listEmptyText?: string;
} & Omit<
  FlashListProps<FlashListItem>,
  'data' | 'renderItem' | 'getItemType' | 'keyExtractor' | 'onScroll' | 'ref'
>;

export function SessionCardList({
  sessions,
  isLoading,
  scrollKey,
  pageCommunityId,
  userId,
  onRefresh,
  refreshing,
  sortDirection = 'asc',
  listEmptyText = 'No upcoming sessions',
  ...otherProps
}: Props) {
  const { isMobile } = useMediaQueries();
  const styles = useDynamicStyles(createStyles);
  const flashListRef = useRef<FlashList<FlashListItem>>(null);
  const viewableItemIndicesRef = useRef<number[]>([]);

  // Build a lookup of event_id -> number of sessions for that event
  const eventIdToSessionCountMap = useMemo(() => {
    const map = new Map<string, number>();
    if (!sessions) return map;

    for (const { event_id } of sessions) {
      map.set(event_id, (map.get(event_id) ?? 0) + 1);
    }

    return map;
  }, [sessions]);

  // Use the shared data fetching hook
  const { queryKeys } = useSessionListDataFetching({
    sessions,
    isLoading,
    userId,
  });

  const groupedSessions = useMemo(
    () => groupSessionsByDate(sessions, sortDirection),
    [sessions, sortDirection],
  );

  const flashListData = useMemo(
    () => createFlashListData(groupedSessions),
    [sessions],
  );

  const markedDates = useMemo(
    () => createSessionMarkedDates(sessions),
    [sessions],
  );

  const handleDateSelect = (selectedDate: string) => {
    scrollToFlashListDate({
      selectedDate,
      flashListRef,
      groupedSessions,
      viewableItemIndicesRef,
      sortDirection,
    });
  };

  const handleViewableItemsChanged = ({
    viewableItems,
  }: {
    viewableItems: ViewToken[];
  }) => {
    viewableItemIndicesRef.current = viewableItems
      .map((item) => item.index)
      .filter((item) => item !== null) as number[];
  };

  // Scroll position preservation hook
  const { handleScroll } = useScrollPositionPreservation({
    scrollKey,
    scrollRef: flashListRef,
    isLoading,
    hasData: groupedSessions.length > 0,
  });

  const sectionStatuses = useMemo(() => {
    if (!groupedSessions.length) return [];

    const currentTime = DateTime.now();
    const statuses = [];

    for (let i = 0; i < groupedSessions.length; i++) {
      const section = groupedSessions[i];
      const sectionStartTime = DateTime.fromISO(section.startDateTime);

      // Find the latest end time in this section
      let latestEndTime = DateTime.fromISO(section.sessions[0].end_date!);
      for (const session of section.sessions) {
        const endDateTime = DateTime.fromISO(session.end_date!);
        if (endDateTime > latestEndTime) {
          latestEndTime = endDateTime;
        }
      }

      // Check if this section is happening now
      const isHappeningNow =
        sectionStartTime <= currentTime && currentTime <= latestEndTime;

      if (isHappeningNow) {
        statuses[i] = SessionCardStatus.NOW;
      } else if (sectionStartTime > currentTime) {
        // This is the first upcoming section
        const hoursUntilStart = sectionStartTime.diff(
          currentTime,
          'hours',
        ).hours;
        if (hoursUntilStart <= 24) {
          statuses[i] = SessionCardStatus.NEXT;
        }
        break;
      }
    }

    return statuses;
  }, [groupedSessions]);

  const stickyHeaderIndices = flashListData
    .filter((item) => item.type === 'header')
    .map((item) => item.sectionIndex * 2);

  const renderItem = ({ item }: { item: FlashListItem }) => {
    const sectionStatus = sectionStatuses[item.sectionIndex];

    if (item.type === 'header') {
      return (
        <CardListDateHeader
          startDateTime={item.startDateTime}
          onDateSelect={handleDateSelect}
          markedDates={markedDates}
          status={sectionStatus}
          minDate={
            sortDirection === 'asc' ? DateTime.now().toISODate() : undefined
          }
          maxDate={
            sortDirection === 'desc' ? DateTime.now().toISODate() : undefined
          }
        />
      );
    }

    const isOneColumn = isMobile || item.sessions?.length === 1;

    return (
      <View style={styles.nestedFlashListWrapper}>
        <View
          style={
            isOneColumn
              ? styles.oneColumnListWrapper
              : styles.twoColumnListWrapper
          }
        >
          <MasonryFlashList
            data={item.sessions}
            renderItem={({ item: session }) => (
              <CacheAwareConfirmedSessionCard
                style={styles.card}
                sessionId={session.id}
                isSingleSessionEvent={
                  eventIdToSessionCountMap.get(session.event_id) === 1
                }
                pageCommunityId={pageCommunityId}
              />
            )}
            keyExtractor={(session) => session.id}
            showsVerticalScrollIndicator={false}
            numColumns={isOneColumn ? 1 : 2}
            key={String(isOneColumn)}
            estimatedItemSize={300}
          />
        </View>
      </View>
    );
  };

  return (
    <CacheAvailabilityProvider queryKeys={queryKeys}>
      <FlashList
        ref={flashListRef}
        data={flashListData}
        renderItem={renderItem}
        getItemType={(item: FlashListItem) => item.type}
        keyExtractor={(item) =>
          item.type === 'header'
            ? `header-${item.sectionIndex}`
            : `section-list-${item.sectionIndex}`
        }
        onScroll={handleScroll}
        scrollEventThrottle={100}
        refreshControl={
          onRefresh ? (
            <RefreshControl
              refreshing={refreshing ?? false}
              onRefresh={onRefresh}
              tintColor={Colors.dark.text}
              colors={[Colors.dark.text]}
              progressBackgroundColor={Colors.dark.opacity20}
            />
          ) : undefined
        }
        ListEmptyComponent={
          isLoading ? (
            <Loader style={styles.loader} />
          ) : (
            <TextReg style={styles.emptyStateText}>{listEmptyText}</TextReg>
          )
        }
        ListFooterComponent={<View style={styles.footer} />}
        stickyHeaderIndices={stickyHeaderIndices}
        estimatedItemSize={300}
        onViewableItemsChanged={handleViewableItemsChanged}
        {...otherProps}
      />
    </CacheAvailabilityProvider>
  );
}

const createStyles = ({ isMobile }: WithResponsive) =>
  StyleSheet.create({
    sectionContainer: {
      width: '100%',
      alignItems: 'center',
    },
    nestedFlashListWrapper: {
      flexDirection: 'row',
      justifyContent: 'center',
      marginHorizontal: isMobile ? 0 : CARD_MARGIN,
    },
    oneColumnListWrapper: {
      maxWidth: 600,
      flex: 1,
    },
    twoColumnListWrapper: {
      maxWidth: 1200,
      flex: 1,
    },
    card: {
      margin: CARD_MARGIN,
      flex: 1,
    },
    emptyStateText: {
      textAlign: 'center',
      fontSize: 16,
      color: Colors.dark.text,
      opacity: 0.7,
      margin: 40,
    },
    footer: {
      height: 80,
    },
    loader: {
      marginTop: 50,
    },
  });
